import KEY from "../clientVariables.json";

export default function generateSourceJobsQuery(
  keywords = {},
  start = 0,
  rows = 20,
  filters = {},
  sort = "posted_date desc",        // can be string | {field, order} | Array
) {
  const baseUrl = `${KEY?.solr}/${KEY?.candidate_jobs_solr}/select`;

  // --- helpers ---
  const isNonEmptyStr = (v) => typeof v === "string" && v.trim().length > 0;
  const isNonEmptyArr = (v) => Array.isArray(v) && v.length > 0;

  // Escape Solr special chars but NOT wildcard * ? that we add ourselves
  const solrEscape = (s) =>
    String(s).replace(/([+\-&|!(){}\[\]^"~:\\/])/g, "\\$1"); // leave * and ? alone

  // Remove surrounding quotes if user typed "Linkedin"
  const stripOuterQuotes = (s) => {
    const t = String(s).trim();
    return (t.startsWith('"') && t.endsWith('"')) ? t.slice(1, -1) : t;
  };

  // Build a partial-match term: "Java Developer" -> (*Java* AND *Developer*)
  const toPartialAND = (val) => {
    const clean = stripOuterQuotes(val);
    const tokens = clean.split(/\s+/).filter(Boolean).map(solrEscape);
    if (!tokens.length) return null;
    if (tokens.length === 1) return `*${tokens[0]}*`;
    return `(${tokens.map(t => `*${t}*`).join(" AND ")})`;
  };

  // Accept sort as string | object | array and normalize to "field dir[,field dir]"
  const normalizeSort = (s) => {
    if (isNonEmptyStr(s)) return s;
    if (Array.isArray(s)) {
      const parts = s.map((item) => {
        if (isNonEmptyStr(item)) return item;
        if (item && item.field) return `${item.field} ${(item.order || item.dir || "asc")}`;
        return null;
      }).filter(Boolean);
      return parts.length ? parts.join(", ") : "posted_date desc";
    }
    if (s && typeof s === "object" && s.field) {
      return `${s.field} ${(s.order || s.dir || "asc")}`;
    }
    return "posted_date desc";
  };

  const fqParams = [];

  // --- keywords -> fq (partial match by default, with special 'remote') ---
  Object.keys(keywords || {}).forEach((field) => {
    const raw = keywords[field];
    if (!isNonEmptyStr(raw)) return;

    if (field.toLowerCase() === "location") {
      const v = stripOuterQuotes(raw).toLowerCase();
      if (v === "remote") {
        fqParams.push("remote:1");
      } else {
        const term = toPartialAND(raw);
        if (term) fqParams.push(`location:${term}`);
      }
      return;
    }

    const term = toPartialAND(raw);
    if (term) fqParams.push(`${field}:${term}`);
  });

  // --- filters (optional, partial by default) ---
  if (isNonEmptyStr(filters.company)) {
    const term = toPartialAND(filters.company);
    if (term) fqParams.push(`company_name:${term}`);
  }

  if (isNonEmptyArr(filters.job_titles)) {
    const parts = filters.job_titles
      .filter(isNonEmptyStr)
      .map(toPartialAND)
      .filter(Boolean)
      .map((t) => `jobtitle:${t}`);
    if (parts.length) fqParams.push(`(${parts.join(" OR ")})`);
  }

  if (isNonEmptyArr(filters.job_types)) {
    const parts = filters.job_types
      .filter(isNonEmptyStr)
      .map(toPartialAND)
      .filter(Boolean)
      .map((t) => `jobtype_name:${t}`);
    if (parts.length) fqParams.push(`(${parts.join(" OR ")})`);
  }

  // --- Date window (default last 7 days) ---
  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const toISODate = (d) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

  const ndays = Number.isFinite(filters.days) ? Math.max(0, filters.days) : 7;
  const from = new Date(today);
  from.setUTCDate(today.getUTCDate() - ndays);
  const fromISO = `${toISODate(from)}T00:00:00Z`;
  const toISO = `${toISODate(today)}T23:59:59Z`;
  fqParams.push(`posted_date:[${fromISO} TO ${toISO}]`);

  // --- Build final query ---
  const params = new URLSearchParams();
  params.set("q", "*:*");
  params.set("q.op", "OR");
  params.set("start", String(start));
  params.set("rows", String(rows));
  params.set("sort", normalizeSort(sort));
  fqParams.forEach((fq) => params.append("fq", fq));

  return `${baseUrl}?${params.toString()}`;
}
